import { useCallback, useEffect, useState } from "react";
import { fetchLastGroups } from "../groupsApi";

/**
 * Inner async loader; kept outside the hook so it stays under 25 lines.
 * @param {Object} args - { signal, setGroups, setError, setLoading }
 */
async function loadLast({ signal, setGroups, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchLastGroups({ signal });
        if (!signal.aborted) setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
        if (signal.aborted) return;
        setError(err?.response?.data || "Impossible de charger les derniers groupes");
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Custom hook fetching the 10 most recently created groups.
 * @param {boolean} [enabled=true] - When false, skips fetching (lazy until needed)
 * @returns {{ groups: Array, loading: boolean, error: any, refresh: Function }}
 */
function useLastGroups(enabled = true) {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        if (!enabled) return undefined;
        const controller = new AbortController();
        loadLast({ signal: controller.signal, setGroups, setError, setLoading });
        return () => controller.abort();
    }, [tick, enabled]);

    const refresh = useCallback(() => setTick((t) => t + 1), []);

    return { groups, loading, error, refresh };
}

export default useLastGroups;
